import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {ISquadsStore} from "data/stores/squads/squds.store";
import {IMatch, IRound} from "data/types/entities";
import {last, first, flatMap, take} from "lodash";
import {Empty, Nullable} from "data/types/generics";
import {MatchUtils} from "data/utils/match";
import {RefObject, useRef} from "react";
import {RoundType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {PRE_SEASON_ROUND_MAP} from "data/constants";
import {SquadUtils} from "data/utils/squad";

export interface IWidgetController extends ViewController {
	get scrollRef(): Nullable<RefObject<HTMLDivElement>>;

	get isLoading(): boolean;

	get matches(): IMatch[];

	get currentMatch(): Empty<IMatch>;

	get currentRound(): Empty<IRound>;

	get currentRoundName(): string;

	get currentRoundPrefix(): string;

	get isOpenFilters(): boolean;

	get hasNextRound(): boolean;

	get hasPrevRound(): boolean;

	setCurrentMatch(matchId: number): void;

	onLeftClick: () => void;
	onRightClick: () => void;
	toggleFilters: () => void;
}

@injectable()
export class WidgetController implements IWidgetController {
	@observable _selectedMatch: number | null = null;
	@observable _filtersState: boolean = false;
	@observable _isLoading: boolean = true;
	@observable _dirrectionScroll: number = 0;
	private _filterDisposer: Nullable<ReturnType<typeof reaction>> = null;
	private _scrollRef = useRef<Nullable<HTMLDivElement>>(null);

	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.LocalizationStore) private i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get scrollRef() {
		return this._scrollRef;
	}

	get isLoading() {
		return this._isLoading;
	}

	get hasNextRound() {
		return !!this._roundsStore.nextRound;
	}

	get hasPrevRound() {
		return !!this._roundsStore.prevRound;
	}

	get matches() {
		const matches = this._roundsStore.currentRound?.tournaments ?? [];
		const currentMatches = matches.filter(SquadUtils.filterBySquadID);

		if (SquadUtils.SELECTED_SQUAD) {
			return take([...currentMatches, ...this.nextMatches], 4);
		}

		return currentMatches;
	}

	get currentMatch() {
		if (this._selectedMatch) {
			return this.matches.find((match) => match.id === this._selectedMatch);
		}

		const actualMatch = this.matches.find(
			(match) => MatchUtils.isPlaying(match) || MatchUtils.isScheduled(match)
		);

		if (this._dirrectionScroll === -1) {
			return last(this.matches);
		}

		if (this._dirrectionScroll === 1) {
			return first(this.matches);
		}

		return actualMatch || last(this.matches);
	}

	get currentRound() {
		return this._roundsStore.currentRound;
	}

	get currentRoundName() {
		const round = this._roundsStore.currentRound;
		if (round) {
			const roundLetter = PRE_SEASON_ROUND_MAP[round.number];
			if (round.type === RoundType.PRE && roundLetter) {
				return roundLetter;
			}
			return round.number.toString();
		}

		return "-";
	}

	get currentRoundPrefix() {
		const round = this._roundsStore.currentRound;
		if (round) {
			return {
				[RoundType.POST]: this.i18n.t("widget.filter.playoffs", "playoffs"),
				[RoundType.PRE]: this.i18n.t("widget.filter.preseason", "preseason"),
				[RoundType.REG]: this.i18n.t("widget.filter.week", "week"),
			}[round.type];
		}
		return this.i18n.t("widget.filter.week", "week");
	}

	setCurrentMatch(matchId: number) {
		this._selectedMatch = matchId;
	}

	get isOpenFilters() {
		return this._filtersState;
	}

	set isOpenFilters(value: boolean) {
		this._filtersState = value;
	}

	toggleFilters = () => {
		this._filtersState = !this._filtersState;
	};

	onLeftClick = () => {
		if (this._scrollRef.current) {
			if (this._scrollRef.current?.scrollLeft === 0) {
				this.toPrevRound();
			}

			this._scrollRef.current?.scrollBy({
				left: -this._scrollRef.current?.clientWidth,
				behavior: "smooth",
			});
		}
	};

	onRightClick = () => {
		const current = this._scrollRef.current;
		if (current) {
			const {scrollWidth, scrollLeft, clientWidth} = current;

			current.scrollBy({
				left: this._scrollRef.current?.clientWidth,
				behavior: "smooth",
			});

			if (scrollWidth - scrollLeft - clientWidth <= 20 || !scrollWidth) {
				this.toNextRond();
			}
		}
	};

	dispose(): void {
		this._filterDisposer?.();
	}

	init() {
		void this.fetchData();

		this._filterDisposer = reaction(
			() => this.matches,
			() => (this.isOpenFilters = false)
		);
	}

	private async fetchData() {
		this._isLoading = true;
		try {
			await Promise.all([this._roundsStore.fetchRounds(), this._squadsStore.fetchSquads()]);

			this.checkSelectedSquad();
		} catch (e) {
			console.log(e);
		} finally {
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

	private toPrevRound = () => {
		const prevRound = this._roundsStore.prevRound;
		if (prevRound) {
			this._dirrectionScroll = -1;
			this._roundsStore.selectedRoundID = prevRound.id;
		}
	};

	private toNextRond = () => {
		const nextRound = this._roundsStore.nextRound;

		if (nextRound) {
			this._dirrectionScroll = 1;
			this._roundsStore.selectedRoundID = nextRound.id;
		}
	};

	private checkSelectedSquad() {
		if (!SquadUtils.SELECTED_SQUAD) {
			return;
		}

		if (!this._squadsStore.getSquadById(SquadUtils.SELECTED_SQUAD)) {
			console.error(`Squad does not exist - ${SquadUtils.SELECTED_SQUAD}`);
			SquadUtils.SELECTED_SQUAD = null;
		}
	}

	private get nextMatches() {
		const rounds = this._roundsStore.listBySquad;
		const currentRoundIndex = rounds.findIndex((it) => it.id === this.currentRound?.id);
		const nextRounds = this._roundsStore.listBySquad.filter(
			(_round, index) => index > currentRoundIndex
		);

		return flatMap(nextRounds, (round) => {
			return round.tournaments.filter(SquadUtils.filterBySquadID);
		});
	}
}
